import React, { ReactElement } from 'react';
import { Box } from '@mui/material';
import { Event } from 'services/api';
import { EventCardRail, EventCardRailSkeleton } from './EventCardRail';
import { FeedSectionHeader } from './FeedSectionHeader';
import { RailMarker } from './RailMarker';
import { DESKTOP_RAIL_MAX, RAIL_GAP, formatRailDay } from './feedTokens';

export interface UpcomingRailProps {
  events: Event[];
  loading: boolean;
  desktop: boolean;
  hostingIds: ReadonlySet<string>;
  loadingId?: string | null;
  onOpen: (event: Event) => void;
  onAction: (event: Event) => void;
  onSeeAll: () => void;
}

export const UpcomingRail = ({
  events,
  loading,
  desktop,
  hostingIds,
  loadingId,
  onOpen,
  onAction,
  onSeeAll,
}: UpcomingRailProps): ReactElement | null => {
  if (!loading && events.length === 0) return null;

  return (
    <Box aria-label="Upcoming events" sx={{ width: '100%' }}>
      <FeedSectionHeader
        title="Upcoming events"
        onViewMore={loading ? undefined : onSeeAll}
      />
      <Box sx={{ mt: desktop ? '16px' : '12px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[0, 1, 2].map(item => (
              <Box key={item} sx={{ display: 'flex', gap: `${RAIL_GAP}px` }}>
                {desktop && <Box sx={{ width: '44px', flexShrink: 0 }} />}
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: desktop ? `${DESKTOP_RAIL_MAX}px` : '100%',
                  }}
                >
                  <EventCardRailSkeleton desktop={desktop} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            {events.map((event, index) => {
              const day = event.date.slice(0, 10);
              const previousDay =
                index > 0 ? events[index - 1].date.slice(0, 10) : null;
              const firstOfDay = day !== previousDay;
              const label = formatRailDay(day);
              const showLine = index < events.length - 1;
              return (
                <Box
                  key={event.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: `${RAIL_GAP}px`,
                  }}
                >
                  <RailMarker
                    variant={firstOfDay ? 'day' : 'continue'}
                    weekday={label.weekday}
                    day={label.day}
                    showLine={showLine}
                  />
                  <Box
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      width: '100%',
                      maxWidth: desktop ? `${DESKTOP_RAIL_MAX}px` : '100%',
                      pb: showLine ? '16px' : 0,
                    }}
                  >
                    <EventCardRail
                      event={event}
                      desktop={desktop}
                      hosting={hostingIds.has(event.id)}
                      loading={loadingId === event.id}
                      onOpen={() => onOpen(event)}
                      onAction={() => onAction(event)}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};
